import { useMemo } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { selectOnboardingTrainingSummary } from "../../../redux/selector";
import { selectCandidateProfile } from "../../../../auth/redux/selector";
import { OnboardingTrainingPlatform } from "../../course-menu-screen/type";

export const useOnboardingTrainingCommon = () => {
  const candidateOnboardingTrainingSummary = useSelector(selectOnboardingTrainingSummary);
  const recruitmentConfig = useSelector((state: any) => state.onboarding.configs.Recruitment);
  const candidateProfile = useSelector(selectCandidateProfile);

  // Get training platform configuration
  const trainingPlatform = useMemo(() => {
    return get(recruitmentConfig, "trainingPlatform", OnboardingTrainingPlatform.COE);
  }, [recruitmentConfig]);

  // Check if component should be visible - configurationEnabled logic from app
  const configurationEnabled = useMemo(() => {
    const isTrainingModuleEnabled = recruitmentConfig?.enableOnboardingTrainingModule;
    return (
      isTrainingModuleEnabled &&
      recruitmentConfig?.onboardingTrainingVisibleOn?.includes?.("CandidateProfile")
    );
  }, [recruitmentConfig]);

  // Check if courses are configured - coursesConfigured logic from app
  const coursesConfigured = useMemo(() => {
    if (trainingPlatform !== OnboardingTrainingPlatform.NEW) {
      const trainingSummary = candidateOnboardingTrainingSummary?.trainingSummary;
      if (!trainingSummary) return false;
      const requiredToAttend = trainingSummary?.requiredToAttend !== false;
      const isCandidateInProgress = candidateProfile?.status === "IN_PROGRESS";
      // For web, we may not have training status logic, so simplified check
      return requiredToAttend && isCandidateInProgress;
    } else {
      const configuredCourses =
        recruitmentConfig?.onboardingTraining?.courses?.map((course: any) => course.courseId) || [];
      return configuredCourses.length > 0;
    }
  }, [candidateOnboardingTrainingSummary, candidateProfile, trainingPlatform, recruitmentConfig]);

  // Overall visibility check
  const onboardingTrainingShouldShow = useMemo(() => {
    return configurationEnabled && coursesConfigured;
  }, [configurationEnabled, coursesConfigured]);

  // Check if training is active - onboardingTrainingIsActive logic from app
  const onboardingTrainingIsActive = useMemo(() => {
    const trainingSummary = candidateOnboardingTrainingSummary?.trainingSummary;
    if (!trainingSummary) {
      return false;
    }
    const isActive = trainingSummary?.active === true;

    if (trainingPlatform !== OnboardingTrainingPlatform.COE) {
      return isActive;
    }
    return true;
  }, [candidateOnboardingTrainingSummary, trainingPlatform]);

  // Legacy compatibility for enableOnboardingTraining
  const enableOnboardingTraining = useMemo(() => {
    return onboardingTrainingShouldShow;
  }, [onboardingTrainingShouldShow]);

  return {
    trainingPlatform,
    configurationEnabled,
    coursesConfigured,
    onboardingTrainingShouldShow,
    onboardingTrainingIsActive,
    enableOnboardingTraining,
    candidateOnboardingTrainingSummary,
    recruitmentConfig,
    candidateProfile,
  };
};
